/**
 * Fullscreen preference.
 *
 * The burger menu only records the preference; it never enters fullscreen itself. The app
 * enters it when the referee starts the warmup or the match: browsers refuse fullscreen
 * without a gesture, and a reload (every new match reloads the page) leaves it, so the
 * request has to come back with each match. Turning the preference off leaves fullscreen.
 *
 * Browsers without the Fullscreen API for pages (Safari on iPhone) get no menu entry.
 */

const PREFERENCE_KEY = 'fullscreenPreferred';

type FullscreenDocument = Document & {
  webkitFullscreenEnabled?: boolean;
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void> | void;
};

type FullscreenElement = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void> | void;
};

export function isFullscreenSupported(): boolean {
  const doc = document as FullscreenDocument;
  return Boolean(doc.fullscreenEnabled || doc.webkitFullscreenEnabled);
}

function isFullscreenActive(): boolean {
  const doc = document as FullscreenDocument;
  return Boolean(doc.fullscreenElement || doc.webkitFullscreenElement);
}

export function isFullscreenPreferred(): boolean {
  try {
    return localStorage.getItem(PREFERENCE_KEY) === 'true';
  } catch {
    return false;
  }
}

export function setFullscreenPreferred(preferred: boolean): void {
  try {
    if (preferred) {
      localStorage.setItem(PREFERENCE_KEY, 'true');
    } else {
      localStorage.removeItem(PREFERENCE_KEY);
    }
  } catch {
    // Private mode, disabled storage: the preference lasts until the next reload at best
  }
  if (!preferred && isFullscreenActive()) {
    const doc = document as FullscreenDocument;
    const exit = doc.exitFullscreen?.bind(doc) ?? doc.webkitExitFullscreen?.bind(doc);
    Promise.resolve(exit?.()).catch(() => {});
  }
}

/** To call from a click handler: enters fullscreen when the preference is on. */
export function enterFullscreenIfPreferred(): void {
  if (!isFullscreenPreferred() || !isFullscreenSupported() || isFullscreenActive()) return;
  const root = document.documentElement as FullscreenElement;
  const request = root.requestFullscreen?.bind(root) ?? root.webkitRequestFullscreen?.bind(root);
  // A refusal (no gesture, policy) leaves the page as it is: fullscreen is a comfort, never a blocker
  Promise.resolve(request?.()).catch(() => {});
}
